import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { CategoriesSectionData } from '@app/utils/interfaces/categories-interface';
import { Category } from '@app/utils/interfaces/categories.interface';
import { EditorBodyAdapter } from '@app/utils/interfaces/editor-interface';
import { TranslatePipe } from '@ngx-translate/core';
import { tap } from 'rxjs';

@Component({
  selector: 'app-categories-editor-body',
  standalone: true,
  imports: [IconComponent, ReactiveFormsModule, TranslatePipe],
  templateUrl: './categories-editor-body.component.html',
  styleUrl: './categories-editor-body.component.scss',
})
export class CategoriesEditorBodyComponent
  implements OnInit, EditorBodyAdapter<CategoriesSectionData>
{
  public data = input.required<CategoriesSectionData>();
  public allCategories = input<Category[]>([]);
  public searchedCategories = input<Category[]>([]);
  public isPreviewMode = input(false);
  public searchChange = output<string>();

  public readonly IconName = IconName;
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  public isValid = signal(true);
  public hasChanges = signal(false);
  public canPreview = signal(true);

  public searchFocused = signal(false);

  private removedCategoryIds = signal<number[]>([]);
  private removedBaseCategories = signal<Category[]>([]);

  private addedCategories = signal<Category[]>([]);
  protected availableSearchOptions = signal<Category[]>([]);

  protected editForm!: FormGroup;

  private baseSubCategories = computed(() => {
    return this.allCategories().filter((cat) => cat.parentId !== null);
  });

  public proceedCategories = computed(() => {
    const base = this.baseSubCategories();
    const added = this.addedCategories();
    const removedIds = this.removedCategoryIds();

    const filteredAdded = added.filter((cat) => !removedIds.includes(cat.id));

    const filteredBase = base.filter(
      (cat) => !removedIds.includes(cat.id) && !added.some((addedCat) => addedCat.id === cat.id),
    );

    return [...filteredAdded, ...filteredBase];
  });

  constructor() {
    effect(
      () => {
        const freshResults = this.searchedCategories();
        const alreadyAdded = this.addedCategories();

        const deletedBaseItems = this.removedBaseCategories();
        const combinedPool = [...freshResults, ...deletedBaseItems];

        const uniquePool = combinedPool.filter(
          (cat, index, self) => self.findIndex((c) => c.id === cat.id) === index,
        );

        const filteredResults = uniquePool.filter(
          (cat) => !alreadyAdded.some((added) => added.id === cat.id),
        );

        this.availableSearchOptions.set(filteredResults);
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.editForm = this.fb.group({
      sectionName: this.fb.control(this.data().sectionType, [Validators.required]),
      search: this.fb.control(''),
    });

    this.editForm
      .get('search')
      ?.valueChanges.pipe(
        tap((value) => {
          const trimmedValue = (value ?? '').trim();
          this.searchChange.emit(trimmedValue);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.availableSearchOptions.set([...this.searchedCategories()]);
  }

  public saveData(): void {
    this.onSubmit();
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public resetForm(): void {}
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public markAllAsTouched(): void {}

  public getSubmitPayload(): CategoriesSectionData {
    return {} as CategoriesSectionData;
  }

  public getVisibilityValue(): boolean {
    return true;
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  public setVisibilityValue(value: boolean): void {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onSubmit(): void {}

  public removeCategory(category: Category) {
    const isNewlyAdded = this.addedCategories().some((cat) => cat.id === category.id);

    if (isNewlyAdded) {
      this.addedCategories.update((categories) =>
        categories.filter((cat) => cat.id !== category.id),
      );
    } else {
      this.removedBaseCategories.update((categories) => [category, ...categories]);
      this.availableSearchOptions.update((options) => [category, ...options]);
      this.removedCategoryIds.update((ids) => [...ids, category.id]);
    }
  }

  public addCategory(newCategory: Category) {
    return this.addedCategories.update((currentList) => [newCategory, ...currentList]);
  }

  public onSearchFocus(): void {
    this.searchFocused.set(true);
  }

  public onSearchBlur(): void {
    this.searchFocused.set(false);
  }

  public selectCategory(category: Category): void {
    this.addedCategories.update((categories) => {
      return [category, ...categories];
    });

    this.removedCategoryIds.update((ids) => {
      return ids.filter((id) => {
        return id !== category.id;
      });
    });

    this.removedBaseCategories.update((categories) => {
      return categories.filter((cat) => {
        return cat.id !== category.id;
      });
    });

    this.searchInput()?.nativeElement.blur();

    this.searchFocused.set(false);
  }
}
